import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateLeadMutation } from "store/api/api";
import { Card, Col, Divider, Form, notification, Row, Tag, Typography, Flex, Avatar } from "antd";
import getStartedHero from "../../images/sweatbase-header.jpg";
import circuitImage from "../../images/circuit.png";
import brush from "../../images/brush.png";
import rocketLaunch from "../../images/rocket-launch.png";
import sweatbaseLeadingBrands from "../../images/sweatbase-leading-brands.png";
import { validateEmail } from "helpers/Form/commonFormValidations";
import sweatbaseDeviceThree from "../../images/sweatbase-device3.png";
import verticalImage from "../../images/vertical-image.png";
import sweatbaseVideo from "../../images/sb-video.png";
import RokuIcon from "../../images/svgIcons/roku";
import AndroidIcon from "images/svgIcons/android";
import IosIcon from "images/svgIcons/ios";
import gridSquare from "../../images/grid-square.png";
import code from "../../images/code-orange.png";
import arrow from "../../images/arrow-orange.png";
import { FormInput } from "components/Common/Form/FormInput";
import { FormSelect } from "components/Common/Form/FormSelect";
import { FullLogoIcon } from "components/Icons";
import { Button } from "components/Common/Button";
import useWindowDimensions from "helpers/hooks/useWindowDimensions";
import StyledGetStarted from "./styles";
import Icon from "@ant-design/icons";
import { BLOG_ENTRIES } from "utils/dummyBlogEntries";
import EntryCard from "./EntryCard";
import PhoneInput from "antd-phone-input";
import PhoneFrame from "./PhoneFrame";
import { validateRequiredEncodedLength } from "helpers/Form/commonFormValidations";

const REQUEST_DEMO_LABEL = "Request a Demo";

function scrollToSection(section_id) {
	const section = document.getElementById(section_id);
	if (section) {
		section.scrollIntoView({ behavior: "smooth" });
	}
}

const Header = () => {
	const navigate = useNavigate();
	return (
		<div className="header">
			<div style={{ zIndex: 3 }}>
				<FullLogoIcon />
			</div>
			<div className="actions">
				<Button onClick={() => navigate("/gym-search")} className="transparent-btn">
					Find My Gym
				</Button>
				<Button onClick={() => scrollToSection("request-demo")} className="orange-btn">
					{REQUEST_DEMO_LABEL}
				</Button>
			</div>
		</div>
	);
};

const HeaderSection = () => {
	const navigate = useNavigate();
	const SUBTITLE = "Seamlessly integrate video and licensed music to power engaging fitness content in your app.";
	return (
		<div
			className="sb-get-started__main-section"
			style={{
				backgroundImage: `url(${getStartedHero})`,
			}}
		>
			<Header />
			<div className="body">
				<p className="title clash-display">
					Your Engine for <span className="highlight">High-Impact</span> Fitness Experiences
				</p>
				<p className="subtitle">{SUBTITLE}</p>
				<div className="actions">
					<Button onClick={() => navigate("/gym-search")} className="transparent-btn" data-id="btnWelcomeFindMyGym">
						Find My Gym
					</Button>
					<Button onClick={() => scrollToSection("request-demo")} className="orange-btn">
						{REQUEST_DEMO_LABEL}
					</Button>
				</div>
			</div>
		</div>
	);
};

const PlaftformIntegrationSection = () => {
	const { Title, Paragraph } = Typography;
	const ITEMS = [
		{
			title: "All In One Solution",
			content: "Access to video and trending music we all love in one API.",
			icon: rocketLaunch,
		},
		{
			title: "Scalable & Reliable",
			content: "Built for brands handling thousands of users seamlessly.",
			icon: gridSquare,
		},
		{
			title: "Customizable",
			content: "Adapt the look, feel, and functionality to match your brand.",
			icon: brush,
		},
	];

	const PLATFORMS = [
		{
			title: "iPhone",
			icon: <Icon component={IosIcon} />,
			isIcon: true,
		},
		{
			title: "android",
			icon: <Icon component={AndroidIcon} />,
			isIcon: true,
		},
		{
			title: "Roku",
			icon: <Icon component={RokuIcon} />,
			isIcon: true,
		},
		{
			title: "+20 more",
			icon: "",
			isIcon: false,
		},
	];

	return (
		<Card className="sb-get-started__integration-section">
			<Row className="sb-get-started__integration-section__first-child" gutter={16}>
				<Col className="sb-get-started__integration-section__first-child__integration-card" lg={10} sm={24}>
					<div
						className="circuit-section"
						style={{
							backgroundImage: `url(${circuitImage})`,
						}}
					>
						<Title className="any-platform clash-display">
							Effortless <span className="highlight clash-display">Integration</span> for Any Platform
						</Title>
					</div>
					<Flex gap="4px 0" wrap>
						{PLATFORMS.map((platform) => (
							<Tag
								key={platform.title}
								style={{ padding: "3px 8px", height: 28 }}
								icon={platform.icon}
								bordered={false}
							>
								{!platform.isIcon && platform.title}
							</Tag>
						))}
					</Flex>
					<Paragraph className="paragraph-subtitle">
						Our API provides everything needed to embed fully licensed music and fitness video content directly into
						your app with minimal setup time.
					</Paragraph>
					<Button onClick={() => scrollToSection("request-demo")} className="black-btn request-demo" variant="solid">
						{REQUEST_DEMO_LABEL}
					</Button>
				</Col>
				<Col lg={2} sm={24}></Col>
				<Col lg={12} sm={24}>
					<img style={{ width: "100%" }} src={sweatbaseVideo} alt="Video" />
				</Col>
			</Row>
			<Row style={{ gap: "40px 0" }} className="sb-get-started__integration-section__second-child" gutter={16}>
				{ITEMS.map((item) => (
					<Col key={item.title} lg={8} sm={24}>
						<Avatar style={{ margin: "0 auto", display: "flex" }} size={44} src={item.icon} />
						<Card
							className="card-no-shadow"
							bordered={false}
							title={item.title}
							variant="outlined"
							styles={{
								title: {
									fontSize: 24,
									fontWeight: 700,
									fontFamily: "DM Sans",
								},
								header: {
									border: 0,
								},
								body: {
									padding: "2px 24px",
									fontSize: 16,
									fontWeight: 300,
								},
							}}
						>
							{item.content}
						</Card>
					</Col>
				))}
			</Row>
		</Card>
	);
};

function GetStarted() {
	const { width } = useWindowDimensions();
	const [disabled, setDisabled] = useState(false);

	const [createLeadTrigger, { isLoading }] = useCreateLeadMutation();

	const [form] = Form.useForm();
	const onFinishForm = useCallback(
		async (values) => {
			if (disabled) {
				return;
			}
			setDisabled(true);
			const { data, error } = await createLeadTrigger({
				first_name: values.firstName,
				last_name: values.lastName,
				email_address: values.email,
				phone_number: `+${values.phone?.countryCode}${values.phone?.areaCode}${values.phone?.phoneNumber}`,
				business_type: values.businessType,
				member_count: values.memberCount,
			});
			if (data) {
				form.resetFields();
				return notification.success({
					message: "You successfully signed up for Beta access",
				});
			}
			if (error) {
				setDisabled(false);
				return notification.error({
					message: "We encountered a problem submitting your details. Please try again.",
				});
			}
		},
		[createLeadTrigger, disabled, form]
	);

	const TrustedBySection = () => {
		return (
			<div className="sb-get-started__trusted-by-section">
				<div className="gradient"></div>
				<Row style={{ flexWrap: "wrap-reverse" }}>
					<Col className="sb-get-started__trusted-by-section__image-container" lg={12} sm={24}>
						<Row gutter={16} style={{ marginBottom: 16 }}>
							<Col className="vertical-container" xs={24} md={12} sm={24}>
								<div className="vertical-video-bg" style={{ backgroundImage: `url(${verticalImage})` }}>
									<PhoneFrame
										url="https://stream.mux.com/bjzxGxm352A6jxga7RYSx9nJfT31V00ZSYREADwTEOqg.m3u8"
										controls={false}
										height={430}
										width={201}
										className="horizontal-video"
										vertical
									/>
								</div>
								{/** <img className="vertical" src={sweatbaseDeviceOne} alt="Sweatbase" /> */}
							</Col>
							<Col className="vertical-container" xs={24} md={12} sm={24}>
								<div className="vertical-container">
									<div className="vertical-video-bg" style={{ backgroundImage: `url(${verticalImage})` }}>
										<PhoneFrame
											url="https://stream.mux.com/f6VVfQSX01Yz7WVk1s8Ccfshp5E7luk02gc601FUQEOpNE.m3u8"
											controls={false}
											height={430}
											width={201}
											className="horizontal-video"
											vertical
										/>
									</div>
								</div>
							</Col>
						</Row>
						<Row justify="center">
							<Col className="horizontal-container" xs={24} sm={24} md={24}>
								<div style={{ backgroundImage: `url(${sweatbaseDeviceThree})` }} className="horizontal-video-bg">
									<PhoneFrame
										url="https://stream.mux.com/MqLAZaMe8rSu37r00gLvBuIyeTyDzWS7fSz9dMD02cJxQ.m3u8"
										controls={false}
										width={488}
										height={230}
										className="horizontal-video"
									/>
								</div>
							</Col>
						</Row>
					</Col>
					<Col className="sb-get-started__trusted-by-section__info-container" lg={12} sm={24}>
						<div
							className="leadingbrands"
							md={12}
							sm={24}
							style={{
								backgroundImage: `url(${sweatbaseLeadingBrands})`,
							}}
						/>
						<Card className="info-container-card">
							<h1 className="title clash-display">
								Trusted by <span className="highlight clash-display">Leading Brands </span>In Fitness
							</h1>
							<p>
								Our content engine powers top fitness apps, delivering seamless experiences and boosting user
								engagement.
							</p>
							<Button
								onClick={() => scrollToSection("request-demo")}
								className="black-btn request-demo"
								variant="solid"
							>
								{REQUEST_DEMO_LABEL}
							</Button>
						</Card>
					</Col>
				</Row>
			</div>
		);
	};

	const BlogItemsSection = () => {
		return (
			<div className="sb-get-started__blog-items-section">
				<h1 className="title clash-display">Latest from the blog</h1>
				<Row style={{ margin: "40px 0px" }} gutter={[16, 16]}>
					{BLOG_ENTRIES.map((entry) => (
						<Col key={entry.title} xs={24} sm={12} md={8} lg={6}>
							<EntryCard
								key={entry.title}
								thumbnail={entry.thumbnail}
								title={entry.title}
								category={entry.category}
								slug={entry.slug}
							/>
						</Col>
					))}
				</Row>
			</div>
		);
	};

	const ContactSection = () => {
		const { Meta } = Card;
		const GUIDE_LIST = [
			{
				title: "Guided Onboarding",
				description: "Personalized support through every integration step.",
				icon: arrow,
			},
			{
				title: "Comprehensive Resources",
				description: "Developer guides, best practices, and responsive support.",
				icon: code,
			},
		];

		return (
			<div id="request-demo" className="sb-get-started__contact-section">
				<div className="gradient"></div>
				<Row>
					<Col className="sb-get-started__contact-section__info-container" md={12} sm={24}>
						<Card className="info-container-card">
							<h1 className="title clash-display">
								Join the Waitlist for <span className="highlight clash-display">Exclusive</span> VIP Support
							</h1>
							<p>
								SweatBase provides developer resources and dedicated support, making it easy to go live with
								high-quality content in days, not weeks.
							</p>
							{GUIDE_LIST.map((item) => (
								<Meta
									key={item.title}
									avatar={<Avatar src={item.icon} />}
									title={item.title}
									description={item.description}
								/>
							))}
						</Card>
					</Col>
					<Col className="sb-get-started__contact-section__form-container" md={12} sm={24}>
						<Card className="form-container-card">
							<h1 style={{ fontWeight: 600 }} className="title clash-display">
								Be the first to know
							</h1>
							<Form form={form} onFinish={onFinishForm} layout="vertical" style={{ marginTop: 16 }}>
								<div
									style={{
										marginBottom: 12,
										display: "flex",
										gap: width < 968 ? "8px" : "32px",
										flexWrap: width < 968 ? "wrap" : null,
									}}
								>
									<FormInput
										name="firstName"
										placeholder="Enter first name..."
										label={<span style={{ color: "#4F5661" }}>First Name</span>}
										rules={[
											{ required: true, message: "This field is required" },
											() => ({
												validator(_, value) {
													const validationStatus = validateRequiredEncodedLength(value, 255);
													if (value && validationStatus) {
														return Promise.reject(new Error(validationStatus));
													}
													return Promise.resolve();
												},
											}),
										]}
										style={{ background: "white" }}
									/>
									<FormInput
										name="lastName"
										placeholder="Enter last name..."
										label={<span style={{ color: "#4F5661" }}>Last Name</span>}
										rules={[
											{ required: true, message: "This field is required" },
											() => ({
												validator(_, value) {
													const validationStatus = validateRequiredEncodedLength(value, 255);
													if (value && validationStatus) {
														return Promise.reject(new Error(validationStatus));
													}
													return Promise.resolve();
												},
											}),
										]}
										style={{ background: "white" }}
									/>
								</div>
								<div
									style={{
										marginBottom: 12,
										display: "flex",
										gap: width < 700 ? "8px" : "32px",
										flexWrap: width < 700 ? "wrap" : null,
									}}
								>
									<FormInput
										name="email"
										placeholder="Enter a valid email address..."
										label={<span style={{ color: "#4F5661" }}>Email Address</span>}
										rules={[
											{ required: true, message: "This field is required" },
											({ getFieldValue }) => ({
												validator: (_, value) => {
													if (!value?.trim()) {
														return Promise.resolve();
													}
													const isInValid = validateEmail(value);
													if (!isInValid) {
														return Promise.resolve();
													}
													return Promise.reject(new Error(isInValid));
												},
											}),
										]}
										style={{ background: "white" }}
									/>
								</div>
								<div
									style={{
										marginBottom: 12,
									}}
								>
									<Form.Item
										name="phone"
										rules={[
											() => ({
												validator(_, v) {
													const value = `+${v?.countryCode}${v?.areaCode}${v?.phoneNumber}`;
													if (!v || !value) {
														return Promise.reject(new Error("This field is required"));
													}
													if (!v?.areaCode && !v?.phoneNumber) {
														return Promise.resolve();
													}
													if (!v?.valid()) {
														return Promise.reject(new Error("Please enter a valid phone number."));
													}
													return Promise.resolve();
												},
											}),
										]}
										style={{ marginBottom: 0 }}
										label={<span style={{ color: "#4F5661" }}>Phone Number</span>}
									>
										<PhoneInput distinct placeholder="Enter phone number..." name="phone" />
									</Form.Item>
								</div>
								<div
									style={{
										marginBottom: 12,
										display: "flex",
										gap: width < 968 ? "8px" : "32px",
										flexWrap: width < 968 ? "wrap" : null,
									}}
								>
									<FormSelect
										name="businessType"
										label={<span style={{ color: "#4F5661" }}>Type of business</span>}
										placeholder="Business type"
										rules={[{ required: true, message: "This field is required" }]}
										style={{ height: "46px", textAlign: "left" }}
										options={[
											{ value: "trainer", label: "Personal Trainer" },
											{ value: "independent", label: "Independent Gym" },
											{ value: "corporate", label: "Corporate Gym" },
											{ value: "franchise", label: "Gym Franchise" },
										]}
									/>
									<FormSelect
										name="memberCount"
										label={<span style={{ color: "#4F5661" }}>Member Count</span>}
										placeholder="0 - 10 members"
										size="large"
										style={{ height: "46px", textAlign: "left" }}
										rules={[{ required: true, message: "This field is required" }]}
										options={[
											{ value: "0-10", label: "0 - 10 members" },
											{ value: "11-100", label: "11 - 100 members" },
											{ value: "101-300", label: "101 - 300 members" },
											{ value: "301-1000", label: "301 - 1,000 members" },
											{ value: "1000+", label: "Over 1,000 members" },
										]}
									/>
								</div>
								<Form.Item required={false}>
									<Button loading={isLoading} disabled={disabled} className="black-btn" type="submit">
										{REQUEST_DEMO_LABEL}
									</Button>
								</Form.Item>
							</Form>
						</Card>
					</Col>
				</Row>
			</div>
		);
	};

	return (
		<StyledGetStarted screenWidth={width}>
			<div className="sb-get-started">
				<HeaderSection />
				<PlaftformIntegrationSection />
				<TrustedBySection />
				<ContactSection />
				<BlogItemsSection />
				<Footer />
			</div>
		</StyledGetStarted>
	);

	function Footer() {
		return (
			<div className="sb-get-started__footer">
				<div className="">
					<FullLogoIcon />
				</div>
				<Divider className="divider" size="small" />
				<div className="copyright">© Copyright 2012 - {new Date().getFullYear()}. All Rights Reserved.</div>
			</div>
		);
	}
}

export default GetStarted;

